import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { clikArtCategoriesQueryOptions } from '@/api/hooks/use-clikart-categories'

const IDLE_TIMEOUT_MS = 5000
const FALLBACK_DELAY_MS = 2000

/**
 * Sends the ClikArt categories request once the editor has its first spread, so the backend's
 * 24h cache is warm (and this session's query cache is filled) before anyone opens the ClikArt
 * panel. Runs when the browser is idle so it never competes with the spread load. The prefetch
 * respects `staleTime: Infinity`, so it's a no-op once data exists, and it never throws — if it
 * fails, the panel just fetches on first open as before.
 */
export function useClikArtCategoriesWarmup(enabled: boolean) {
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!enabled) return undefined

    const warm = () => {
      void queryClient.prefetchQuery(clikArtCategoriesQueryOptions())
    }

    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(warm, { timeout: IDLE_TIMEOUT_MS })

      return () => window.cancelIdleCallback(idleId)
    }

    const timeoutId = window.setTimeout(warm, FALLBACK_DELAY_MS)

    return () => window.clearTimeout(timeoutId)
  }, [enabled, queryClient])
}
